import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Building, Plus, Pencil, Trash2, Search, DollarSign } from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';

export default function CorporateClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [billingDialog, setBillingDialog] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', credit_terms_days: 30, credit_limit: 10000, contract_terms: '', notes: '' });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/corporate-clients', { params: { search } });
      setClients(res.data.clients || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(fetchClients, 300); return () => clearTimeout(t); }, [fetchClients]);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/corporate-clients/${editing}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/corporate-clients', form);
        toast.success('Client created');
      }
      setDialogOpen(false); setEditing(null);
      setForm({ name: '', contact_person: '', email: '', phone: '', address: '', credit_terms_days: 30, credit_limit: 10000, contract_terms: '', notes: '' });
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving'); }
  };

  const handleEdit = (c) => {
    setEditing(c.client_id);
    setForm({ name: c.name, contact_person: c.contact_person || '', email: c.email || '', phone: c.phone || '', address: c.address || '', credit_terms_days: c.credit_terms_days || 30, credit_limit: c.credit_limit || 10000, contract_terms: c.contract_terms || '', notes: c.notes || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this corporate client?')) return;
    try { await api.delete(`/corporate-clients/${id}`); toast.success('Deleted'); fetchClients(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleViewBilling = async (client) => {
    setBillingDialog(client);
    try {
      const res = await api.get(`/corporate-clients/${client.client_id}/billing`);
      setBillingData(res.data);
    } catch (e) { toast.error('Failed to load billing'); }
  };

  return (
    <div className="space-y-6" data-testid="corporate-clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Building className="h-6 w-6 text-primary" /> Corporate Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage corporate accounts and billing</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', contact_person: '', email: '', phone: '', address: '', credit_terms_days: 30, credit_limit: 10000, contract_terms: '', notes: '' }); setDialogOpen(true); }} data-testid="add-corporate-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-corporate" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Credit Terms</TableHead>
                <TableHead>Credit Limit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.client_id} data-testid={`corporate-row-${c.client_id}`}>
                  <TableCell>
                    <div><span className="font-medium">{c.name}</span></div>
                    {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{c.contact_person || '—'}</div>
                    {c.phone && <div className="text-xs text-muted-foreground">{c.phone}</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline">{c.credit_terms_days} days</Badge></TableCell>
                  <TableCell className="font-medium">{fmtCurrency(c.credit_limit || 0)}</TableCell>
                  <TableCell><Badge variant={c.status === 'active' ? 'default' : 'secondary'}>{c.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewBilling(c)} title="View Billing"><DollarSign className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.client_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && clients.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No corporate clients found. Add one to get started.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Corporate Client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Company Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="corp-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div className="space-y-1"><Label>Credit Terms (days)</Label><Input type="number" value={form.credit_terms_days} onChange={(e) => setForm({...form, credit_terms_days: parseInt(e.target.value) || 30})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Credit Limit ($)</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({...form, credit_limit: parseFloat(e.target.value) || 0})} data-testid="corp-limit-input" /></div>
              <div className="space-y-1"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label>Contract Terms</Label><Textarea value={form.contract_terms} onChange={(e) => setForm({...form, contract_terms: e.target.value})} className="h-16" /></div>
            <Button className="w-full" onClick={handleSave} disabled={!form.name} data-testid="save-corporate-btn">{editing ? 'Update' : 'Create'} Client</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Billing Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={() => { setBillingDialog(null); setBillingData(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Billing — {billingDialog?.name}</DialogTitle></DialogHeader>
          {billingData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total Sales</p><p className="text-xl font-bold">{billingData.billing_summary.total_sales}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total Billed</p><p className="text-xl font-bold text-primary">{fmtCurrency(billingData.billing_summary.total_billed)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Discounts Given</p><p className="text-xl font-bold text-orange-500">{fmtCurrency(billingData.billing_summary.total_discount)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Customers</p><p className="text-xl font-bold">{billingData.billing_summary.unique_customers}</p></CardContent></Card>
              </div>
              {billingData.sales.length > 0 ? (
                <Table>
                  <TableHeader><TableRow><TableHead>Sale ID</TableHead><TableHead>Customer</TableHead><TableHead>Employee ID</TableHead><TableHead>Total</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {billingData.sales.slice(0, 20).map(s => (
                      <TableRow key={s.sale_id}>
                        <TableCell className="text-xs font-mono">{s.sale_id?.slice(-8)}</TableCell>
                        <TableCell className="text-sm">{s.customer_name || '—'}</TableCell>
                        <TableCell className="text-xs">{s.corporate_employee_id || '—'}</TableCell>
                        <TableCell className="text-sm font-medium">{fmtCurrency(s.total)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-center text-muted-foreground py-6">No sales recorded for this client yet.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
